// ============================================================
// types/usuario.types.ts
// ============================================================

export type Rol = 'ADMIN' | 'DIRECTOR' | 'SECRETARIA' | 'DOCENTE' | 'ESTUDIANTE' | 'PADRE' | 'AUXILIAR' | 'PSICOLOGO';
export type EstadoUsuario = 'ACTIVO' | 'INACTIVO' | 'SUSPENDIDO';
export type TipoContrato = 'NOMBRADO' | 'CONTRATADO' | 'CAS' | 'PRACTICANTE';
export type GradoAcademico = 'BACHILLER' | 'LICENCIADO' | 'MAGISTER' | 'DOCTOR' | 'TECNICO';
export type Sexo = 'M' | 'F';

export const SEXO_LABEL: Record<Sexo, string> = {
  M: 'Masculino',
  F: 'Femenino',
};

/** Devuelve "Masculino" / "Femenino" / "—" según el valor recibido del backend. */
export function fmtSexo(s: Sexo | null | undefined): string {
  if (s === 'M' || s === 'F') return SEXO_LABEL[s];
  return '—';
}

// ── Datos profesionales del docente ──────────────────────────

export interface DatosDocente {
  idDatosDocente?: number;
  usuarioId: number;
  sexo: Sexo | null;
  gradoAcademico: GradoAcademico | null;
  carrera: string | null;
  numeroColegiatura: string | null;
  tipoContrato: TipoContrato | null;
  anioIngreso: number | null;
}

export interface DatosDocentePayload {
  sexo: Sexo | null;
  gradoAcademico: GradoAcademico | null;
  carrera: string | null;
  numeroColegiatura: string | null;
  tipoContrato: TipoContrato | null;
  anioIngreso: number | null;
}

// ── Catálogos ─────────────────────────────────────────────────

export interface RolItem {
  idRol:  number;
  nombre: string;   // 'ADMIN' | 'DOCENTE' | etc.
}

export interface AñoEscolar {
  idAnioEscolar: number;
  anio: number;
  fase: 'REGULAR' | 'RECUPERACION';
  fechaInicio: string | null;
  fechaFin: string | null;
  activo: boolean;
}

export interface Grado {
  idGrado: number;
  nombre: string;
  nivel: string;
  activo: boolean;
}

export interface Seccion {
  idSeccion: number;
  nombre: string;
  gradoId: number;
  capacidad: number;
  activo: boolean;
}

export interface Curso {
  idCurso: number;
  nombre: string;
  descripcion: string;
  activo: boolean;
}

// ── Payload de registro/modificación de usuario ───────────────
// Campos exactos que espera el backend

export interface UsuarioPayload {
  username: string;
  password?: string;        // solo en creación
  nombres: string;
  apellidos: string;
  email: string;
  dni: string;
  telefono: string;
  direccion: string | null;
  fechaNacimiento: string | null;
  codigoSiagie: string | null;  // solo para ESTUDIANTE
  fotoPerfil: null;         // siempre null desde el frontend
  idRol: number;            // el backend espera idRol, no el string del rol
  activo: boolean;
  sexo: Sexo | null;        // 'M' | 'F' | null — Jackson valida estricto
}

// Mapa de Rol → idRol según la tabla del backend
// El backend resuelve el id real desde GET /roles (catalogStorage.getIdRol).
// Estos valores son fallback estático cuando el catálogo no está disponible.
export const ROL_ID: Record<Rol, number> = {
  ADMIN:      1,
  DOCENTE:    2,
  ESTUDIANTE: 3,
  PADRE:      4,
  SECRETARIA: 5,
  DIRECTOR:   6,
  AUXILIAR:   7,
  PSICOLOGO:  8,
};

// ── Respuesta de la API para un item de la lista ──────────────

export interface UsuarioListItem {
  idUsuario: number;
  username: string;
  nombres: string;
  apellidos: string;
  email: string;
  dni: string;
  telefono: string | null;
  direccion: string | null;
  fechaNacimiento: string | null;
  codigoSiagie: string | null;
  fotoPerfil: string | null;
  idRol: number;
  rol: Rol;
  activo: boolean;
  fechaCreacion: string | null;
  ultimoAcceso: string | null;
  sexo: Sexo | null;
}

// ── Registro masivo ───────────────────────────────────────────

export type RolMasivo = 'ESTUDIANTE' | 'DOCENTE';

export interface UsuarioMasivoItem {
  username:     string;
  password:     string;
  nombres:      string;
  apellidos:    string;
  email:        string;
  dni:          string;
  rol:          RolMasivo;
  codigoSiagie: string | null;
  sexo:         Sexo | null;     // 'M' | 'F' o null
}

export interface UsuarioMasivoError {
  fila:     number;
  username: string;
  motivo:   string;
}

export interface UsuarioMasivoResponse {
  totalEnviados: number;
  totalCreados:  number;
  totalErrores:  number;
  creados:       UsuarioListItem[];
  errores:       UsuarioMasivoError[];
}

// ── Paginación Spring Boot ────────────────────────────────────
// Nuevo formato: metadatos en data.page.* (no flat)

export interface PageMeta {
  totalElements: number;
  totalPages:    number;
  number:        number;   // página actual (0-based)
  size?:         number;
}

export interface PagedResponse<T> {
  content: T[];
  page:    PageMeta;
  // Helpers calculados si se necesitan:
  // isLast  = page.number >= page.totalPages - 1
  // isFirst = page.number === 0
}

// ── Respuesta genérica del backend ────────────────────────────

export interface ApiResponse<T = null> {
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
}

// ── Estado del formulario de usuario (interno del form) ───────

export interface FormState {
  nombres: string;
  apellidos: string;
  dni: string;
  email: string;
  telefono: string;
  fechaNacimiento: string;
  direccion: string;
  codigoSiagie: string;
  username: string;
  password: string;
  confirmarPassword: string;
  rol: Rol;
  idRol: number;
  activo: boolean;
  cargo: string;
  area: string;
  sexo: '' | Sexo;     // '' = sin seleccionar (se envía como null)
}

export const FORM_INITIAL: FormState = {
  nombres: '', apellidos: '', dni: '', email: '',
  telefono: '', fechaNacimiento: '', direccion: '',
  codigoSiagie: '',
  username: '', password: '', confirmarPassword: '',
  rol: 'ADMIN', idRol: ROL_ID['ADMIN'], activo: true,
  cargo: '', area: '',
  sexo: '',
};